import type { ReviewType } from '../api';

export type FreshnessFilter = 'all' | 'due' | 'overdue';

export const reviewDays: Record<ReviewType, number> = {
  aws: 100,
  github: 100,
  'core-metadata': 180,
};

export function minimumDaysByFilter(reviewType: ReviewType): Record<FreshnessFilter, number> {
  return { all: 0, due: reviewDays[reviewType] - 10, overdue: reviewDays[reviewType] };
}

export interface ReviewFreshness {
  color: 'success' | 'warning' | 'error';
  /** Material Symbols ligature name, distinct per severity, color isn't the only signal (WCAG 1.4.1). */
  icon: string;
  label: string;
  days: number;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const FRESH = { color: 'success', icon: 'check_circle', label: 'Recently reviewed' } as const;
const DUE = { color: 'warning', icon: 'warning', label: 'Due for review' } as const;
const OVERDUE = { color: 'error', icon: 'error', label: 'Overdue for review' } as const;

export function reviewFreshness(reviewedOn: string, reviewType: ReviewType, now: Date = new Date()): ReviewFreshness {
  const days = Math.floor((now.getTime() - new Date(reviewedOn).getTime()) / MS_PER_DAY);
  const thresholds = minimumDaysByFilter(reviewType);
  const band = days < thresholds.due ? FRESH : days < thresholds.overdue ? DUE : OVERDUE;
  return { ...band, days };
}

// reviewedOn is a date-only string (YYYY-MM-DD), parsed as UTC midnight. Formatting in the
// viewer's local zone could shift it a calendar day either way, so pin the format to UTC too.
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** e.g. "Sep 2, 2026". */
export function formatReviewedOnDate(reviewedOn: string): string {
  return dateFormatter.format(new Date(reviewedOn));
}

/** e.g. "(20 days ago)", "(1 day ago)", "(today)". */
export function formatDaysAgo(days: number): string {
  if (days === 0) return '(today)';
  if (days === 1) return '(1 day ago)';
  return `(${days} days ago)`;
}

/** e.g. "Sep 2, 2026 (20 days ago)". */
export function formatReviewedOn(reviewedOn: string, now: Date = new Date()): string {
  const days = Math.floor((now.getTime() - new Date(reviewedOn).getTime()) / MS_PER_DAY);
  return `${formatReviewedOnDate(reviewedOn)} ${formatDaysAgo(days)}`;
}
